import React from 'react'
import { X } from 'lucide-react'
import { theme } from '../../theme'

// `size` is omitted from the native attributes so it can carry a style variant
// rather than the HTML input's numeric character width. It is destructured out
// below and never forwarded to the DOM element.
export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string
  error?: string
  helperText?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  fullWidth?: boolean
  size?: 'small' | 'medium' | 'large'
  allowClear?: boolean
  onClear?: () => void
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      disabled,
      fullWidth = true,
      size = 'medium',
      allowClear = false,
      onClear,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const containerStyle: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.xs,
      width: fullWidth ? '100%' : 'auto',
    }

    const labelStyle: React.CSSProperties = {
      ...theme.typography.bodyBold,
      color: theme.colors.text.primary,
    }

    // Size-based styling
    const sizeStyles = {
      small: {
        padding: '4px 8px',
        fontSize: '12px',
        lineHeight: '16px',
        iconOffset: '32px',
      },
      medium: {
        padding: '8px 10px',
        fontSize: '14px',
        lineHeight: '20px',
        iconOffset: '38px',
      },
      large: {
        padding: '12px 12px',
        fontSize: '16px',
        lineHeight: '24px',
        iconOffset: '40px',
      },
    }

    const currentSize = sizeStyles[size]

    // Determine if we should show clear button
    const hasValue = value !== undefined && value !== null && value !== ''
    const showClearButton = allowClear && hasValue && !disabled

    // Use clear button if allowClear is enabled and has value, otherwise use rightIcon
    const effectiveRightIcon = showClearButton ? (
      <X
        size={size === 'small' ? 14 : 16}
        style={{ cursor: 'pointer' }}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (onChange) {
            const syntheticEvent = {
              target: { value: '' },
              currentTarget: { value: '' },
            } as React.ChangeEvent<HTMLInputElement>
            onChange(syntheticEvent)
          }
          onClear?.()
        }}
      />
    ) : rightIcon

    const inputWrapperStyle: React.CSSProperties = {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    }

    const inputStyle: React.CSSProperties = {
      width: '100%',
      padding: currentSize.padding,
      paddingLeft: leftIcon ? currentSize.iconOffset : undefined,
      paddingRight: effectiveRightIcon ? currentSize.iconOffset : undefined,
      border: `1px solid ${error ? theme.colors.error : theme.colors.border}`,
      borderRadius: theme.borderRadius.md,
      fontSize: currentSize.fontSize,
      lineHeight: currentSize.lineHeight,
      fontWeight: 400,
      backgroundColor: disabled ? theme.colors.background : theme.colors.surface,
      color: theme.colors.text.primary,
      outline: 'none',
      transition: `all ${theme.animations.duration.fast}ms`,
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? 'not-allowed' : 'text',
      boxSizing: 'border-box',
    }

    const iconStyle: React.CSSProperties = {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      color: theme.colors.text.tertiary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }

    const leftIconStyle: React.CSSProperties = {
      ...iconStyle,
      left: theme.spacing.sm,
    }

    const rightIconStyle: React.CSSProperties = {
      ...iconStyle,
      right: theme.spacing.sm,
    }

    const helperStyle: React.CSSProperties = {
      ...theme.typography.caption,
      color: error ? theme.colors.error : theme.colors.text.tertiary,
    }

    // Ensure value is always a string (never null/undefined) to avoid React warnings
    const safeValue = value ?? ''

    // Generate unique className for this input size
    const inputClassName = `pod-ops-input pod-ops-input-${size}`

    return (
      <div style={containerStyle}>
        <style>{`
        .pod-ops-input {
          padding: ${currentSize.padding} !important;
        }
        .pod-ops-input.has-left-icon {
          padding-left: ${currentSize.iconOffset} !important;
        }
        .pod-ops-input.has-right-icon {
          padding-right: ${currentSize.iconOffset} !important;
        }
      `}</style>
        {label && <label style={labelStyle}>{label}</label>}
        <div style={inputWrapperStyle}>
          {leftIcon && <div style={leftIconStyle}>{leftIcon}</div>}
          <input
            ref={ref}
            className={`${inputClassName}${leftIcon ? ' has-left-icon' : ''}${effectiveRightIcon ? ' has-right-icon' : ''}`}
            style={inputStyle}
            disabled={disabled}
            value={safeValue}
            onChange={onChange}
            {...props}
          />
          {effectiveRightIcon && <div style={rightIconStyle}>{effectiveRightIcon}</div>}
        </div>
        {(error || helperText) && (
          <div style={helperStyle}>{error || helperText}</div>
        )}
      </div>
    )
  })

Input.displayName = 'Input'
